<script setup lang="ts">
import { useI18n } from 'vue-i18n'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import { Tooltip, TooltipTrigger, TooltipContent } from '@/components/ui/tooltip'
import Flag from '@/components/Flag.vue'
import SideHeading from '@/components/SideHeading.vue'
import InsightsInfoTip from '@/components/InsightsInfoTip.vue'
import { formatMoneyWithSymbol } from '@/lib/money'
import { gradeConfig } from '@/lib/product'
import {
    formatCount,
    formatDelta,
    formatGenerations,
    formatPct,
    sideConfigKey,
    type InsightsCompetitorSide,
    type InsightsSide,
} from '@/lib/insights'

/**
 * One side's competitors for the week (ADR-027), already ordered by the API: resale
 * cheapest vs market first, trade-in highest payer first, the first one tagged "Most
 * aggressive". The customer's own position sits on top when the API gives one.
 */
const props = defineProps<{
    side: InsightsSide
    data: InsightsCompetitorSide
    currencySymbol: string
}>()

const { t, locale } = useI18n()

const GRADES = ['N', 'A', 'B', 'C'] as const

const GRADE_COLOURS: Record<(typeof GRADES)[number], string> = {
    N: 'bg-emerald-500',
    A: 'bg-sky-500',
    B: 'bg-amber-500',
    C: 'bg-rose-500',
}

const columns = [
    { key: 'avg', tip: 'avg' },
    { key: 'vsMarket', tip: 'vsMarket' },
    { key: 'vsYou', tip: 'vsYou' },
    { key: 'share', tip: 'share' },
    { key: 'gradeMix', tip: 'gradeMix' },
    { key: 'yourSkus', tip: 'yourSkus' },
    { key: 'listings', tip: 'clistings' },
    { key: 'coverage', tip: 'coverage' },
]

/**
 * A competitor beating the customer is highlighted: selling cheaper on resale, paying
 * sellers more on trade-in.
 */
const isThreat = (vsYou: number | null): boolean => {
    if (vsYou === null) {
        return false
    }

    return props.side === 'resell' ? vsYou < 0 : vsYou > 0
}
</script>

<template>
    <div class="rounded-xl border bg-card">
        <div class="px-6 pt-5 pb-3">
            <SideHeading :side="sideConfigKey(side)" :title="t(`insights.competitors.tables.${side}`)" size="sm" />
        </div>

        <p
            v-if="data.competitors.length === 0 && data.you === null"
            class="px-6 py-10 text-center text-sm text-muted-foreground"
        >
            {{ t('insights.competitors.empty') }}
        </p>

        <Table v-else>
            <TableHeader>
                <TableRow>
                    <TableHead class="pl-6">{{ t('insights.competitors.columns.competitor') }}</TableHead>
                    <TableHead v-for="column in columns" :key="column.key" class="text-right whitespace-nowrap">
                        <span class="inline-flex items-center gap-1">
                            {{ t(`insights.competitors.columns.${column.key}`) }}
                            <InsightsInfoTip
                                :title="t(`insights.competitors.columns.${column.key}`)"
                                :text="t(`insights.tips.${column.tip}`)"
                            />
                        </span>
                    </TableHead>
                </TableRow>
            </TableHeader>
            <TableBody>
                <TableRow v-if="data.you" class="bg-muted/50 hover:bg-muted/50">
                    <TableCell class="pl-6 font-semibold">{{ t('insights.competitors.you') }}</TableCell>
                    <TableCell class="text-right tabular-nums">
                        {{ formatMoneyWithSymbol(data.you.avg, currencySymbol) }}
                    </TableCell>
                    <TableCell class="text-right tabular-nums">{{ formatDelta(data.you.vs_market_pct) }}</TableCell>
                    <TableCell class="text-right text-muted-foreground">—</TableCell>
                    <TableCell class="text-right text-muted-foreground">—</TableCell>
                    <TableCell class="text-right text-muted-foreground">—</TableCell>
                    <TableCell class="text-right tabular-nums">{{ formatCount(data.you.your_skus, locale) }}</TableCell>
                    <TableCell class="text-right tabular-nums">{{ formatCount(data.you.listings, locale) }}</TableCell>
                    <TableCell class="text-right text-muted-foreground">—</TableCell>
                </TableRow>

                <TableRow v-for="competitor in data.competitors" :key="`${competitor.vendor_id}-${competitor.country}`">
                    <TableCell class="pl-6">
                        <div class="flex items-center gap-2">
                            <Flag :country="competitor.country" />
                            <span class="font-medium whitespace-nowrap">{{ competitor.name }}</span>
                            <Badge v-if="competitor.most_aggressive" variant="warning" class="whitespace-nowrap">
                                {{ t('insights.competitors.mostAggressive') }}
                            </Badge>
                        </div>
                    </TableCell>
                    <TableCell class="text-right tabular-nums">
                        {{ formatMoneyWithSymbol(competitor.avg, currencySymbol) }}
                    </TableCell>
                    <TableCell class="text-right tabular-nums whitespace-nowrap">
                        {{ formatDelta(competitor.vs_market_pct) }}
                    </TableCell>
                    <TableCell
                        class="text-right tabular-nums whitespace-nowrap"
                        :class="isThreat(competitor.vs_you_pct) ? 'font-semibold text-red-600 dark:text-red-400' : ''"
                    >
                        {{ formatDelta(competitor.vs_you_pct) }}
                    </TableCell>
                    <TableCell class="text-right tabular-nums">{{ formatPct(competitor.share_pct, 1) }}</TableCell>
                    <TableCell class="text-right">
                        <Tooltip>
                            <TooltipTrigger as-child>
                                <div class="ml-auto flex h-2 w-24 cursor-help overflow-hidden rounded-full bg-muted">
                                    <div
                                        v-for="grade in GRADES"
                                        :key="grade"
                                        :class="GRADE_COLOURS[grade]"
                                        :style="{ width: `${competitor.grade_mix_pct[grade] ?? 0}%` }"
                                    />
                                </div>
                            </TooltipTrigger>
                            <TooltipContent side="top" class="text-xs">
                                <span class="mb-1 block font-semibold">{{ competitor.name }}</span>
                                <span v-for="grade in GRADES" :key="grade" class="flex items-center gap-2">
                                    <span class="size-2 rounded-sm" :class="GRADE_COLOURS[grade]" />
                                    <span class="flex-1">{{ gradeConfig[grade].label }}</span>
                                    <span class="tabular-nums">{{
                                        formatPct(competitor.grade_mix_pct[grade], 1)
                                    }}</span>
                                </span>
                            </TooltipContent>
                        </Tooltip>
                    </TableCell>
                    <TableCell class="text-right tabular-nums whitespace-nowrap">
                        {{
                            competitor.your_skus === null
                                ? '—'
                                : t('insights.competitors.nOfM', {
                                      n: competitor.your_skus.matched,
                                      m: competitor.your_skus.of,
                                  })
                        }}
                    </TableCell>
                    <TableCell class="text-right tabular-nums">{{
                        formatCount(competitor.listings, locale)
                    }}</TableCell>
                    <TableCell class="text-right whitespace-nowrap">
                        <div class="tabular-nums">
                            {{
                                t('insights.competitors.nOfM', {
                                    n: competitor.coverage.skus,
                                    m: competitor.coverage.of,
                                })
                            }}
                        </div>
                        <div class="text-xs text-muted-foreground">
                            {{
                                formatGenerations(
                                    competitor.coverage.newest_generation,
                                    competitor.coverage.oldest_generation,
                                    t('insights.competitors.to'),
                                )
                            }}
                        </div>
                    </TableCell>
                </TableRow>
            </TableBody>
        </Table>
    </div>
</template>
